import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, CheckCircle2, Package, MapPin, AlertCircle, PartyPopper, RotateCcw, Box, User, AlertTriangle, Zap, ListChecks, Send, X, Trash2, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FGPutAwayClearance = () => {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);
  const binInputRef = useRef(null);
  const staffInputRef = useRef(null);

  const [remaining, setRemaining] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Staff session
  const [staff, setStaff] = useState(null); // { staff_id, staff_name } — primary (team[0])
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}] multi-staff team
  const [sessionStarted, setSessionStarted] = useState(false);
  const [staffScanValue, setStaffScanValue] = useState('');
  const [isValidatingStaff, setIsValidatingStaff] = useState(false);
  const [showChangeStaff, setShowChangeStaff] = useState(false);

  // Modes: scan_usn | scan_bin | bin_active | batch_active
  const [mode, setMode] = useState('scan_usn');
  const [scanValue, setScanValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [pendingItem, setPendingItem] = useState(null);
  const [activeBin, setActiveBin] = useState(null);
  const [binSessionCount, setBinSessionCount] = useState(0);
  const [totalPlaced, setTotalPlaced] = useState(0);
  const [lastAction, setLastAction] = useState(null);

  // 2026-05-22 — SKU-locked batch mode (Fast Path)
  //   Per-USN backend round-trips were ~13s on combos. Batch mode runs the
  //   slow bin-suggestion ONCE per SKU, then queues subsequent USN scans
  //   client-side with zero API calls. Submit pushes the whole batch in
  //   ONE bulk write. See backend /clearance/submit-sku-batch.
  const [batchMode, setBatchMode] = useState(() => {
    try { return localStorage.getItem('fg_putaway_batch_mode') !== 'off'; } catch { return true; }
  });
  const [batchLock, setBatchLock] = useState(null); // {sku, design, shade, bin, slip_id}
  const [batchQueue, setBatchQueue] = useState([]); // array of USN strings (first = first scanned)
  const [batchRejected, setBatchRejected] = useState([]); // [{usn, reason}]
  const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);
  // 2026-05-22 (+1) — eligible USN set for the locked SKU, fetched ONCE
  // when batch lock is established. Used to hard-block wrong-SKU scans
  // instantly (client-side O(1)), with zero per-scan API cost.
  const [eligibleSkuUsns, setEligibleSkuUsns] = useState(null); // Set<string> | null (null = still loading)
  const [eligibleLoadFailed, setEligibleLoadFailed] = useState(false);

  // --- FG Packs: whole-pack put-away + grouping stored pieces into a pack ---
  const [pendingPack, setPendingPack] = useState(null); // {pack_code, member_count, barcode, design_name, shade}
  const [packSize, setPackSize] = useState(5);
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [packPieces, setPackPieces] = useState([]);
  const [packPieceInput, setPackPieceInput] = useState('');
  const [packCode, setPackCode] = useState('');
  const [packScanning, setPackScanning] = useState(false);
  const [packCreating, setPackCreating] = useState(false);
  const packPieceRef = useRef(null);
  const packCodeRef = useRef(null);
  const packLockSku = packPieces[0]?.sku_code || '';


  // Persist batch state to localStorage so a refresh / browser close doesn't
  // lose pending scans. Restored on mount below.
  const persistBatch = useCallback((lock, queue, rejected) => {
    try {
      if (lock && (queue?.length || rejected?.length)) {
        localStorage.setItem('fg_putaway_batch_state', JSON.stringify({ lock, queue, rejected, ts: Date.now() }));
      } else {
        localStorage.removeItem('fg_putaway_batch_state');
      }
    } catch { /* ignore quota errors */ }
  }, []);

  useEffect(() => {
    persistBatch(batchLock, batchQueue, batchRejected);
  }, [batchLock, batchQueue, batchRejected, persistBatch]);

  useEffect(() => {
    try { localStorage.setItem('fg_putaway_batch_mode', batchMode ? 'on' : 'off'); } catch { /* noop */ }
  }, [batchMode]);


  const focusScanInput = useCallback(() => {
    setTimeout(() => scanInputRef.current?.focus(), 80);
  }, []);
  const focusBinInput = useCallback(() => {
    setTimeout(() => binInputRef.current?.focus(), 80);
  }, []);
  const focusStaffInput = useCallback(() => {
    setTimeout(() => staffInputRef.current?.focus(), 80);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/clearance/status');
      setRemaining(response.data.remaining);
      setIsComplete(response.data.is_complete);
    } catch (error) {
      console.error('Failed to load clearance status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    apiClient.get('/wms/fg/packs/config').then((r) => setPackSize(r.data.pack_size || 5)).catch(() => {});
  }, []);

  useEffect(() => {
    if (packDialogOpen) setTimeout(() => packPieceRef.current?.focus(), 120);
  }, [packDialogOpen]);

  useEffect(() => {
    if (!sessionStarted || showChangeStaff) focusStaffInput();
    else if (mode === 'scan_usn' || mode === 'bin_active' || mode === 'batch_active') focusScanInput();
    else if (mode === 'scan_bin') focusBinInput();
  }, [mode, sessionStarted, showChangeStaff, focusScanInput, focusBinInput, focusStaffInput]);

  // === STAFF VALIDATION (multi-staff) ===
  const handleStaffScan = async (e) => {
    e.preventDefault();
    const id = staffScanValue.trim();
    if (!id || isValidatingStaff) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffScanValue('');
      focusStaffInput();
      return;
    }
    setIsValidatingStaff(true);
    try {
      const resp = await apiClient.post('/wms/fg/put-away/clearance/validate-staff', { staff_id: id });
      const member = { staff_id: resp.data.staff_id, staff_name: resp.data.staff_name };
      setStaffList((prev) => [...prev, member]);
      setStaff((prev) => prev || member); // keep first as primary
      setStaffScanValue('');
      toast.success(`Added ${resp.data.staff_name}`);
      focusStaffInput();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid Staff ID');
      setStaffScanValue('');
      focusStaffInput();
    } finally {
      setIsValidatingStaff(false);
    }
  };

  const handleRemoveStaff = (id) => {
    setStaffList((prev) => {
      const next = prev.filter((s) => s.staff_id !== id);
      setStaff(next[0] || null);
      return next;
    });
  };

  const handleStartSession = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }
    setStaff(staffList[0]);
    setSessionStarted(true);
    setShowChangeStaff(false);
  };

  const handleChangeStaff = () => {
    setShowChangeStaff(true);
    setStaffList([]);
    setStaff(null);
    setSessionStarted(false);
    setStaffScanValue('');
  };

  const handleCancelChangeStaff = () => {
    setShowChangeStaff(false);
    setStaffScanValue('');
  };

  // === PLACE USN ===
  const placeUSN = async (item, binCode) => {
    const response = await apiClient.post('/wms/fg/put-away/clearance/place', {
      usn_code: item.usn_code,
      bin_code: binCode,
      slip_id: item.slip_id,
      barcode: item.barcode,
      design_name: item.design_name,
      shade: item.shade,
      staff_id: staff?.staff_id || '',
      staff_name: staff?.staff_name || '',
      staff_ids: staffList.map((s) => s.staff_id),
      staff_index: totalPlaced,
    });
    return response.data;
  };

  // === USN SCAN ===
  const handleUSNScan = async (e) => {
    e.preventDefault();
    const usn = scanValue.trim();
    if (!usn || isProcessing) return;

    // BATCH MODE — once a batch is locked, all USN scans are pure client-side queue
    if (batchMode && batchLock && mode === 'batch_active') {
      handleBatchUSNScan(usn);
      return;
    }

    setIsProcessing(true);
    setScanValue('');
    try {
      const scanResp = await apiClient.post('/wms/fg/put-away/clearance/scan', { usn_code: usn });
      const item = scanResp.data;

      // FG PACK scanned → put the whole pack away to one bin in a single action.
      if (item.is_pack) {
        setPendingPack(item);
        setPendingItem(null);
        setMode('scan_bin');
        toast.info(
          item.suggested_bin
            ? `Pack ${item.pack_code} — ${item.member_count} pc(s). Suggested bin ${item.suggested_bin}.`
            : `Pack ${item.pack_code} — ${item.member_count} pc(s). Scan destination bin.`,
          { duration: 3000 });
        return;
      }

      // Chain redirect: a deactivated USN was scanned and the system
      // resolved it to the live tail. Surface that to the operator so
      // they know what's actually being put away.
      if (item.chain_redirect && item.chain_redirect.live_usn) {
        toast.info(
          `USN ${item.chain_redirect.scanned_usn} was replaced. Putting away live USN ${item.chain_redirect.live_usn}.`,
          { duration: 4000 }
        );
      }

      // BATCH MODE — if same active bin SKU, lock & queue without placement call
      if (batchMode && activeBin && item.barcode === activeBin.barcode) {
        startBatchFromActiveBin(item);
        focusScanInput();
        return;
      }

      if (activeBin && item.barcode === activeBin.barcode) {
        try {
          const placeResp = await placeUSN(item, activeBin.bin_code);
          setTotalPlaced(prev => prev + 1);
          setBinSessionCount(prev => prev + 1);
          setActiveBin(prev => ({ ...prev, bin_qty: placeResp.bin_qty }));
          // Don't set remaining/isComplete from place response — stay in scanning mode
          setLastAction({ usn: item.usn_code, bin: activeBin.bin_code, design: item.design_name, shade: item.shade });
          if (placeResp.slip_completed) toast.success(`Slip ${placeResp.slip_id} completed!`, { duration: 3000 });
        } catch (placeErr) {
          toast.error(placeErr.response?.data?.detail || 'Failed to place');
        }
        focusScanInput();
      } else {
        setPendingItem(item);
        setMode('scan_bin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
      focusScanInput();
    } finally {
      setIsProcessing(false);
    }
  };

  // === BATCH MODE: prefetch eligible USNs for a freshly-locked SKU ===
  const prefetchEligibleUsns = useCallback(async (sku) => {
    if (!sku) return;
    setEligibleSkuUsns(null);
    setEligibleLoadFailed(false);
    try {
      // 2026-05-23 — barcode-only fetch. The locked barcode itself is
      // the unique product identity (design + shade + size + description
      // = one barcode), so no need to pass family params.
      const resp = await apiClient.get('/wms/fg/put-away/clearance/sku-eligible-usns', {
        params: { sku },
      });
      const set = new Set(resp.data.usns || []);
      setEligibleSkuUsns(set);
    } catch (error) {
      console.error('Failed to prefetch eligible USNs:', error);
      setEligibleLoadFailed(true);
      // Leave eligibleSkuUsns = null so the client falls back gracefully:
      // the backend submit endpoint is the safety net.
    }
  }, []);

  // === BATCH MODE: subsequent USN scans (pure client-side) ===
  const handleBatchUSNScan = (rawUsn) => {
    const usn = (rawUsn || '').trim();
    if (!usn || !batchLock) return;
    setScanValue('');

    // Already in queue?
    if (batchQueue.includes(usn)) {
      toast.warning(`USN ${usn} already in queue`, { duration: 1200 });
      focusScanInput();
      return;
    }
    // Already in rejected list?
    if (batchRejected.some(r => r.usn === usn)) {
      toast.warning(`USN ${usn} is already rejected. Delete it first to re-add.`, { duration: 1800 });
      focusScanInput();
      return;
    }
    // 2026-05-22 (+1) — Hard-block wrong-SKU scans.
    // If the eligible set is loaded, do O(1) membership check. If the
    // scanned USN isn't in the locked SKU's eligible set, refuse to
    // queue it and prompt the operator to submit the current batch.
    if (eligibleSkuUsns instanceof Set && eligibleSkuUsns.size > 0 && !eligibleSkuUsns.has(usn)) {
      const lockLabel = `${batchLock.design || ''}${batchLock.shade ? ' / ' + batchLock.shade : ''}`.trim() || batchLock.sku;
      toast.error(
        `USN ${usn} does NOT belong to the locked SKU (${lockLabel}). ` +
        'Submit the current batch first, then scan the new SKU.',
        { duration: 3500 }
      );
      focusScanInput();
      return;
    }
    setBatchQueue(prev => [...prev, usn]);
    setLastAction({ usn, bin: batchLock.bin, design: batchLock.design, shade: batchLock.shade });
    focusScanInput();
  };

  // === BATCH MODE: start batch after first USN+bin scan ===
  const startBatchFromActiveBin = (item) => {
    setBatchLock({
      sku: item.barcode,
      design: item.design_name,
      shade: item.shade || '',
      bin: activeBin.bin_code,
      slip_id: item.slip_id || null,
    });
    setBatchQueue([item.usn_code]);
    setBatchRejected([]);
    setMode('batch_active');
    prefetchEligibleUsns(item.barcode);
    toast.success(`Batch started for ${item.design_name} → ${activeBin.bin_code}`, { duration: 1500 });
  };


  // === BIN SCAN ===
  const handleBinScan = async (e) => {
    e.preventDefault();
    const bin = scanValue.trim();
    if (!bin || isProcessing) return;

    // FG PACK put-away: place the whole pack into this bin in one call
    // (reuses the Table Clearance placement engine server-side).
    if (pendingPack) {
      setIsProcessing(true);
      setScanValue('');
      try {
        const resp = await apiClient.post('/wms/fg/packs/put-away', {
          pack_code: pendingPack.pack_code,
          bin_code: bin,
          staff_id: staff?.staff_id || '',
          staff_name: staff?.staff_name || '',
          staff_ids: staffList.map((s) => s.staff_id),
        });
        const placed = resp.data.placed_count || 0;
        setTotalPlaced((prev) => prev + placed);
        setLastAction({ usn: pendingPack.pack_code, bin, design: pendingPack.design_name, shade: pendingPack.shade });
        toast.success(`Pack ${pendingPack.pack_code}: ${placed} pc(s) stored in ${bin}`, { duration: 2500 });
        setPendingPack(null);
        setMode('scan_usn');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Pack put-away failed');
        focusBinInput();
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Handle bin override (just change active bin, no placement)
    if (pendingItem?.usn_code === '__override__') {
      // 2026-05-24 — Override flow MUST validate the bin too. Previously
      // the operator could override into ANY string (including a bin
      // already storing a different SKU), creating mixed-SKU bins. We
      // call validate-bin with the locked SKU so the backend enforces
      // existence + one-bin-per-SKU before we accept the swap.
      setIsProcessing(true);
      setScanValue('');
      let overrideBinQty = 0;
      try {
        const v = await apiClient.get('/wms/fg/put-away/clearance/validate-bin', {
          params: { bin_code: bin, expected_sku: pendingItem.barcode || '' },
        });
        if (!v.data.valid) {
          toast.error(v.data.reason || `Bin ${bin} cannot be used.`, { duration: 4000 });
          setIsProcessing(false);
          focusBinInput();
          return;
        }
        overrideBinQty = v.data.bin_qty || 0;
      } catch (verr) {
        toast.error(verr.response?.data?.detail || 'Could not validate bin. Try again.');
        setIsProcessing(false);
        focusBinInput();
        return;
      }
      setActiveBin(prev => ({ ...prev, bin_code: bin, bin_qty: overrideBinQty }));
      setBinSessionCount(0);
      setPendingItem(null);
      setMode('bin_active');
      setIsProcessing(false);
      toast.success(`Bin switched to ${bin}`);
      return;
    }

    if (!pendingItem) return;
    setIsProcessing(true);
    setScanValue('');

    // BATCH MODE — validate the bin scan, then lock the batch (instead
    // of single-USN placement). Without validation the scanner happily
    // accepts a USN accidentally scanned into the bin field and uses
    // it as the bin_code → corrupted placements.
    if (batchMode) {
      let batchBinQty = 0;
      try {
        const v = await apiClient.get('/wms/fg/put-away/clearance/validate-bin', {
          params: { bin_code: bin, expected_sku: pendingItem?.barcode || '' },
        });
        if (!v.data.valid) {
          toast.error(v.data.reason || `Bin ${bin} is not valid. Scan the bin barcode.`, { duration: 3500 });
          setIsProcessing(false);
          focusBinInput();
          return;
        }
        batchBinQty = v.data.bin_qty || 0;
      } catch (verr) {
        toast.error(verr.response?.data?.detail || 'Could not validate bin. Try again.');
        setIsProcessing(false);
        focusBinInput();
        return;
      }
      // Lock the batch state with the just-scanned bin and the pending item
      setBatchLock({
        sku: pendingItem.barcode,
        design: pendingItem.design_name,
        shade: pendingItem.shade || '',
        bin: bin,
        slip_id: pendingItem.slip_id || null,
      });
      setBatchQueue([pendingItem.usn_code]);
      setBatchRejected([]);
      setActiveBin({
        bin_code: bin,
        barcode: pendingItem.barcode,
        design_name: pendingItem.design_name,
        shade: pendingItem.shade,
        size: pendingItem.size,
        bin_qty: batchBinQty,
      });
      setLastAction({ usn: pendingItem.usn_code, bin, design: pendingItem.design_name, shade: pendingItem.shade });
      setPendingItem(null);
      setMode('batch_active');
      setIsProcessing(false);
      prefetchEligibleUsns(pendingItem.barcode);
      toast.success(`Batch ready. Bin ${bin} locked for ${pendingItem.design_name}`, { duration: 1500 });
      focusScanInput();
      return;
    }

    try {
      const placeResp = await placeUSN(pendingItem, bin);
      setActiveBin({
        bin_code: bin,
        barcode: pendingItem.barcode,
        design_name: pendingItem.design_name,
        shade: pendingItem.shade,
        size: pendingItem.size,
        bin_qty: placeResp.bin_qty,
      });
      setBinSessionCount(1);
      setTotalPlaced(prev => prev + 1);
      // Don't set remaining/isComplete from place response — stay in scanning mode
      setLastAction({ usn: pendingItem.usn_code, bin, design: pendingItem.design_name, shade: pendingItem.shade });
      setPendingItem(null);
      setMode('bin_active');
      if (placeResp.slip_completed) toast.success(`Slip ${placeResp.slip_id} completed!`, { duration: 3000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid bin');
      focusBinInput();
    } finally {
      setIsProcessing(false);
    }
  };

  // === BATCH MODE: Submit queued USNs to backend (one bulk write) ===
  const handleBatchSubmit = async () => {
    if (!batchLock || batchQueue.length === 0 || isSubmittingBatch) return;
    setIsSubmittingBatch(true);
    try {
      const resp = await apiClient.post('/wms/fg/put-away/clearance/submit-sku-batch', {
        bin_code: batchLock.bin,
        expected_sku: batchLock.sku,
        expected_design: batchLock.design,
        expected_shade: batchLock.shade || '',
        slip_id: batchLock.slip_id || '',
        staff_id: staff?.staff_id || '',
        staff_name: staff?.staff_name || '',
        staff_ids: staffList.map((s) => s.staff_id),
        staff_index: totalPlaced,
        usns: batchQueue,
      });
      const placedList = resp.data.placed || [];
      const rejectedList = resp.data.rejected || [];

      setTotalPlaced(prev => prev + placedList.length);
      setBinSessionCount(prev => prev + placedList.length);

      // Queue keeps only rejected USNs so operator can delete each one and continue
      setBatchQueue(rejectedList.map(r => r.usn));
      setBatchRejected(rejectedList);

      if (placedList.length > 0) {
        toast.success(`Placed ${placedList.length} USN${placedList.length === 1 ? '' : 's'} in ${batchLock.bin}`, { duration: 2200 });
      }
      if (rejectedList.length > 0) {
        toast.error(`${rejectedList.length} USN${rejectedList.length === 1 ? '' : 's'} rejected — review below and delete`, { duration: 3500 });
      }
      if (resp.data.slip_completed) {
        toast.success(`Slip ${resp.data.slip_id} completed!`, { duration: 3000 });
      }
      // If everything placed cleanly, auto-release the batch lock so operator can scan a fresh SKU
      if (rejectedList.length === 0) {
        clearBatchLock();
      }
      focusScanInput();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch submit failed. Retry.');
    } finally {
      setIsSubmittingBatch(false);
    }
  };

  // === BATCH MODE: Delete one rejected USN from the queue ===
  const handleBatchDeleteRejected = (usn) => {
    setBatchQueue(prev => prev.filter(u => u !== usn));
    setBatchRejected(prev => prev.filter(r => r.usn !== usn));
    // If after deleting all rejected, the queue is empty, release lock
    setBatchQueue(prev => {
      const newQ = prev.filter(u => u !== usn);
      if (newQ.length === 0) {
        setBatchLock(null);
        setBatchRejected([]);
        setMode('scan_usn');
      }
      return newQ;
    });
    focusScanInput();
  };

  // === BATCH MODE: Discard entire batch (nothing was submitted yet) ===
  const handleBatchDiscard = () => {
    if (batchQueue.length > 0 || batchRejected.length > 0) {
      const confirmed = window.confirm(`Discard ${batchQueue.length} queued USN${batchQueue.length === 1 ? '' : 's'}? They will NOT be placed.`);
      if (!confirmed) return;
    }
    clearBatchLock();
    toast.info('Batch discarded.');
    focusScanInput();
  };

  const clearBatchLock = () => {
    setBatchLock(null);
    setBatchQueue([]);
    setBatchRejected([]);
    setEligibleSkuUsns(null);
    setEligibleLoadFailed(false);
    setMode('bin_active');
  };

  // === FG PACK: group already-stored pieces (e.g. sales-return) into a pack ===
  const scanPackPiece = async (e) => {
    e?.preventDefault();
    const usn = packPieceInput.trim();
    if (!usn || packScanning) return;
    if (packPieces.some((p) => p.usn_code === usn)) { toast.error(`${usn} already scanned`); setPackPieceInput(''); return; }
    if (packPieces.length >= packSize) { toast.error(`Pack full (${packSize}) — scan the pack code`); setPackPieceInput(''); setTimeout(() => packCodeRef.current?.focus(), 80); return; }
    setPackScanning(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/validate-piece', { usn_code: usn, lock_sku: packLockSku, context: 'stored' });
      const next = [...packPieces, r.data];
      setPackPieces(next);
      setPackPieceInput('');
      if (next.length >= packSize) { toast.success('Pack complete — scan the pack code'); setTimeout(() => packCodeRef.current?.focus(), 80); }
      else setTimeout(() => packPieceRef.current?.focus(), 80);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Piece not eligible');
      setPackPieceInput('');
    } finally { setPackScanning(false); }
  };
  const removePackPiece = (usn) => setPackPieces((prev) => prev.filter((p) => p.usn_code !== usn));
  const submitRepackStored = async (e) => {
    e?.preventDefault();
    const code = packCode.trim();
    if (!code) { toast.error('Scan the pre-printed pack code'); packCodeRef.current?.focus(); return; }
    if (!packPieces.length) { toast.error('Scan at least one stored piece first'); return; }
    if (packCreating) return;
    setPackCreating(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/repack-stored', { pack_code: code, usn_codes: packPieces.map((p) => p.usn_code), staff_id: staff?.staff_id, staff_name: staff?.staff_name });
      toast.success(r.data.message || 'Pack created');
      setPackPieces([]); setPackCode(''); setPackDialogOpen(false);
      focusScanInput();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create pack');
    } finally { setPackCreating(false); }
  };


  // === BIN FULL ===
  const handleBinFull = async () => {
    if (!activeBin || isProcessing) return;
    setIsProcessing(true);
    try {
      const resp = await apiClient.post('/wms/fg/put-away/clearance/bin-full', {
        bin_code: activeBin.bin_code,
        barcode: activeBin.barcode,
        design_name: activeBin.design_name,
        shade: activeBin.shade || '',
        size: activeBin.size || '',
      });

      toast.success(`${activeBin.bin_code} marked full (${resp.data.items_in_bin} pcs). Capacity saved.`);

      if (resp.data.new_suggested_bin) {
        // Auto-set new bin as pending for bin scan
        setPendingItem({
          usn_code: '__bin_full_switch__',
          barcode: activeBin.barcode,
          design_name: activeBin.design_name,
          shade: activeBin.shade,
          size: activeBin.size,
          suggested_bin: resp.data.new_suggested_bin,
          bin_info: resp.data.new_bin_info,
          slip_id: null,
        });
        setActiveBin(null);
        setBinSessionCount(0);
        setMode('scan_bin');
      } else {
        toast.error('No new bin available. Contact supervisor.');
        setActiveBin(null);
        setBinSessionCount(0);
        setMode('scan_usn');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark bin full');
    } finally {
      setIsProcessing(false);
    }
  };

  // === BIN FULL — BIN SCAN (just switch bin, no placement) ===
  const handleBinFullSwitch = async (e) => {
    e.preventDefault();
    const bin = scanValue.trim();
    if (!bin || !pendingItem) return;
    setActiveBin({
      bin_code: bin,
      barcode: pendingItem.barcode,
      design_name: pendingItem.design_name,
      shade: pendingItem.shade,
      size: pendingItem.size,
    });
    setBinSessionCount(0);
    setPendingItem(null);
    setMode('bin_active');
    setScanValue('');
    toast.success(`Switched to bin ${bin}`);
  };

  const handleOverrideBin = () => {
    if (!activeBin) return;
    setPendingItem({
      usn_code: '__override__',
      barcode: activeBin.barcode,
      design_name: activeBin.design_name,
      shade: activeBin.shade,
      size: activeBin.size,
      suggested_bin: null,
      slip_id: null,
    });
    setMode('scan_bin');
    setScanValue('');
  };

  const handleCancelBinScan = () => {
    setPendingItem(null);
    setMode(activeBin ? 'bin_active' : 'scan_usn');
    setScanValue('');
  };

  // Determine correct bin scan handler
  const getBinScanHandler = () => {
    if (pendingItem?.usn_code === '__bin_full_switch__') return handleBinFullSwitch;
    return handleBinScan;
  };

  // === LOADING ===
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <div className="animate-pulse text-[#5A5F6E]">Loading...</div>
      </div>
    );
  }

  // === COMPLETE ===
  if (isComplete) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 p-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-semibold text-sm">Table Clearance</span>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid="clearance-complete">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
            <PartyPopper className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-[#1A1F2E] mb-2">Clearance Complete!</h2>
          <p className="text-sm text-[#5A5F6E] mb-1">All pieces from the processing table have been placed.</p>
          {totalPlaced > 0 && (
            <p className="text-sm text-emerald-600 font-medium">You placed {totalPlaced} pieces this session.</p>
          )}
          <Button onClick={() => navigate('/wms/fg')} className="mt-6 bg-[#1A1F2E]" data-testid="back-to-management">
            Back to FG Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // === STAFF SCAN SCREEN (multi-staff) ===
  if (!sessionStarted || showChangeStaff) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" onClick={() => showChangeStaff ? handleCancelChangeStaff() : navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 p-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-semibold text-sm">{showChangeStaff ? 'Change Staff' : 'Table Clearance'}</span>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-16 h-16 rounded-full bg-[#1A1F2E]/10 flex items-center justify-center mb-5">
            <User className="w-8 h-8 text-[#1A1F2E]" />
          </div>
          <h2 className="text-lg font-bold text-[#1A1F2E] mb-1">
            {showChangeStaff ? 'Switch Staff' : 'Scan Staff ID(s)'}
          </h2>
          <p className="text-sm text-[#5A5F6E] mb-6">Add one or more staff sharing this clearance</p>
          <form onSubmit={handleStaffScan} className="w-full max-w-xs space-y-3">
            <Input
              ref={staffInputRef}
              value={staffScanValue}
              onChange={(e) => setStaffScanValue(e.target.value)}
              placeholder="Scan staff ID badge..."
              className="text-lg h-12 border-[#E5DFD0] bg-white font-mono text-center"
              autoFocus
              disabled={isValidatingStaff}
              data-testid="staff-scan-input"
            />
            <Button
              type="submit"
              disabled={!staffScanValue.trim() || isValidatingStaff}
              className="w-full h-11 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="staff-scan-submit"
            >
              {isValidatingStaff ? 'Validating...' : '+ Add Staff'}
            </Button>
          </form>

          {staffList.length > 0 && (
            <div className="w-full max-w-xs mt-4 space-y-2" data-testid="clearance-staff-list">
              <p className="text-xs text-[#5A5F6E] font-medium">{staffList.length} staff added:</p>
              <div className="flex flex-wrap gap-2">
                {staffList.map((s) => (
                  <div
                    key={s.staff_id}
                    className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full pl-3 pr-1 py-1"
                    data-testid={`clearance-staff-chip-${s.staff_id}`}
                  >
                    <span className="text-xs font-medium text-green-800">{s.staff_name}</span>
                    <span className="text-[10px] text-green-600">({s.staff_id})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveStaff(s.staff_id)}
                      className="w-5 h-5 rounded-full bg-green-100 hover:bg-red-200 flex items-center justify-center text-green-700 hover:text-red-700 text-xs"
                      data-testid={`clearance-staff-remove-${s.staff_id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <Button
                onClick={handleStartSession}
                disabled={staffList.length === 0}
                className="w-full h-11 bg-[#1A1F2E] hover:bg-[#2a2f3e] text-white"
                data-testid="clearance-start-session-btn"
              >
                Start Session ({staffList.length})
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // === MAIN CLEARANCE UI ===
  return (
    <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] px-3 pt-12 pb-2 flex flex-col gap-1 flex-shrink-0" data-testid="clearance-header">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 p-1.5 h-8" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-semibold text-xs truncate">Table Clearance</span>
              <button
                onClick={handleChangeStaff}
                className="text-[10px] text-[#E8DCC8] hover:text-white text-left flex items-center gap-1 truncate"
                data-testid="change-staff-btn"
              >
                <User className="w-2.5 h-2.5 flex-shrink-0" />
                <span className="truncate">{staffList.length > 1 ? `${staffList.length} staff` : `${staff.staff_name} (${staff.staff_id})`}</span>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] flex-shrink-0" data-testid="clearance-header-badges">
            <button
              type="button"
              onClick={() => {
                if (batchLock && batchQueue.length > 0) {
                  toast.warning('Submit or discard the current batch before changing mode.');
                  return;
                }
                setBatchMode(prev => !prev);
                toast.info(`Fast Batch mode ${!batchMode ? 'ON' : 'OFF'}`, { duration: 1200 });
              }}
              className={`px-2 py-1 rounded-full font-bold flex items-center gap-1 transition text-[10px] ${batchMode ? 'bg-emerald-500 text-white' : 'bg-white/10 text-[#E8DCC8] hover:bg-white/20'}`}
              data-testid="batch-mode-toggle"
              title={batchMode ? 'Fast Batch mode ON — tap to switch to one-at-a-time' : 'One-at-a-time mode — tap to enable Fast Batch'}
            >
              <Zap className="w-3 h-3" />
              {batchMode ? 'FAST' : 'SLOW'}
            </button>
            <button
              type="button"
              onClick={() => setPackDialogOpen(true)}
              className="px-2 py-1 rounded-full font-bold flex items-center gap-1 bg-fuchsia-600 text-white hover:bg-fuchsia-700 transition text-[10px]"
              data-testid="clearance-create-pack-btn"
              title="Group stored pieces into a pack"
            >
              <Boxes className="w-3 h-3" />
              PACK
            </button>
            <span className="bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold" data-testid="remaining-count">{remaining}</span>
            {totalPlaced > 0 && (
              <span className="bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5" data-testid="total-placed-count" title="USNs placed this session">
                <CheckCircle2 className="w-2.5 h-2.5" />
                {totalPlaced}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 px-4 py-3 overflow-y-auto space-y-3">

        {/* ACTIVE BIN BANNER */}
        {activeBin && (mode === 'bin_active' || mode === 'scan_usn' || mode === 'batch_active') && (
          <Card className="border-2 border-emerald-400 bg-emerald-50" data-testid="active-bin-banner">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Box className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs text-emerald-700 uppercase tracking-wide font-semibold">Active Bin</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBinFull}
                    disabled={isProcessing}
                    className="text-xs text-amber-700 hover:bg-amber-100 h-7 px-2"
                    data-testid="bin-full-btn"
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Bin Full
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOverrideBin}
                    className="text-xs text-emerald-700 hover:bg-emerald-100 h-7 px-2"
                    data-testid="override-bin-btn"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Change
                  </Button>
                </div>
              </div>
              <p className="text-2xl font-black text-[#1A1F2E] tracking-wider mt-1" data-testid="active-bin-code">
                {activeBin.bin_code}
              </p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-emerald-800">
                  {activeBin.design_name}{activeBin.shade ? ` / ${activeBin.shade}` : ''}
                </p>
                {activeBin.bin_qty != null && (
                  <span
                    className="text-xs font-bold text-emerald-900 bg-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap"
                    data-testid="active-bin-qty"
                  >
                    In bin: {activeBin.bin_qty} pcs
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* USN SCAN INPUT */}
        {(mode === 'scan_usn' || mode === 'bin_active' || mode === 'batch_active') && (
          <form onSubmit={handleUSNScan}>
            <Card className="border-[#E5DFD0] border-2 border-dashed">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Scan className="w-5 h-5 text-[#1A1F2E]" />
                  <p className="text-sm font-semibold text-[#1A1F2E]">
                    {mode === 'batch_active' ? 'Scan next USN (same SKU)' :
                     mode === 'bin_active' ? 'Scan Next USN' : 'Scan USN from Table'}
                  </p>
                  {batchMode && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid="batch-mode-badge">
                      <Zap className="w-3 h-3" /> Fast Batch
                    </span>
                  )}
                </div>
                <Input
                  ref={scanInputRef}
                  value={scanValue}
                  onChange={(e) => setScanValue(e.target.value)}
                  placeholder={mode === 'batch_active' ? 'Scan USN — instant queue…' : 'Scan or type USN code...'}
                  className="text-lg h-12 border-[#E5DFD0] bg-white font-mono"
                  autoFocus
                  disabled={isProcessing || isSubmittingBatch}
                  data-testid="usn-scan-input"
                />
                {isProcessing && (
                  <p className="text-xs text-[#5A5F6E] mt-2 animate-pulse">Processing...</p>
                )}
              </CardContent>
            </Card>
          </form>
        )}

        {/* BATCH ACTIVE — queue + submit panel */}
        {batchMode && batchLock && mode === 'batch_active' && (
          <Card className="border-2 border-emerald-400 bg-white" data-testid="batch-active-panel">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">
                    Batch Queue ({batchQueue.length})
                  </span>
                </div>
                <span className="text-[10px] text-[#5A5F6E]">
                  🔒 {batchLock.design} → {batchLock.bin}
                </span>
              </div>
              {/* SKU guard status — informs operator whether wrong-SKU
                  detection is armed (eligible USN set loaded) or still
                  loading / failed. Backend submit is the safety net either way. */}
              {eligibleSkuUsns === null && !eligibleLoadFailed && (
                <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="sku-guard-loading">
                  ⏳ Loading SKU guard… wrong-SKU scans will be caught at submit until ready.
                </p>
              )}
              {eligibleLoadFailed && (
                <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="sku-guard-failed">
                  ⚠ SKU guard unavailable. Wrong-SKU scans will be flagged at submit.
                </p>
              )}
              {eligibleSkuUsns instanceof Set && eligibleSkuUsns.size > 0 && (
                <p className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="sku-guard-active">
                  ✓ SKU guard active — wrong-SKU scans will hard-block ({eligibleSkuUsns.size} eligible USNs).
                </p>
              )}

              {/* USN list */}
              <div className="max-h-56 overflow-y-auto space-y-1.5 border border-[#E5DFD0] rounded p-2 bg-[#FFFBF5]" data-testid="batch-queue-list">
                {batchQueue.length === 0 ? (
                  <p className="text-xs text-[#5A5F6E] text-center py-2">No USNs in queue yet — scan to add.</p>
                ) : batchQueue.map((u) => {
                  const reject = batchRejected.find(r => r.usn === u);
                  return (
                    <div
                      key={u}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs font-mono ${reject ? 'bg-rose-50 border border-rose-200' : 'bg-emerald-50 border border-emerald-100'}`}
                      data-testid={`batch-queue-row-${u}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {reject ? <X className="w-3 h-3 text-rose-600 flex-shrink-0" /> : <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                          <span className={`truncate ${reject ? 'text-rose-800 line-through' : 'text-emerald-900'}`}>{u}</span>
                        </div>
                        {reject && (
                          <p className="text-[10px] text-rose-700 ml-4 mt-0.5 font-sans" data-testid={`batch-reject-reason-${u}`}>
                            ⚠ {reject.reason}
                          </p>
                        )}
                      </div>
                      {reject && (
                        <button
                          type="button"
                          onClick={() => handleBatchDeleteRejected(u)}
                          className="text-rose-600 hover:bg-rose-100 rounded p-1 flex-shrink-0"
                          data-testid={`batch-delete-${u}`}
                          aria-label={`Delete rejected USN ${u}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={handleBatchSubmit}
                  disabled={batchQueue.length === 0 || isSubmittingBatch || batchRejected.length === batchQueue.length}
                  className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  data-testid="batch-submit-btn"
                >
                  {isSubmittingBatch ? (
                    <span className="animate-pulse">Submitting…</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit ({batchQueue.length - batchRejected.length})
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBatchDiscard}
                  disabled={isSubmittingBatch}
                  className="h-11 px-3 border-[#E5DFD0] text-[#5A5F6E]"
                  data-testid="batch-discard-btn"
                >
                  Discard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* BIN SCAN MODE */}
        {mode === 'scan_bin' && pendingPack && (
          <div className="space-y-3" data-testid="pack-bin-scan-section">
            <Card className="border-2 border-fuchsia-300 bg-fuchsia-50">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Boxes className="w-4 h-4 text-fuchsia-600" />
                  <p className="text-xs text-fuchsia-700 uppercase tracking-wide">Pack Put-Away</p>
                </div>
                <p className="text-lg font-black text-[#1A1F2E]" data-testid="pending-pack-code">{pendingPack.pack_code}</p>
                <p className="text-xs text-[#5A5F6E]">{pendingPack.design_name}{pendingPack.shade ? ` / ${pendingPack.shade}` : ''} · {pendingPack.member_count} pc(s)</p>
              </CardContent>
            </Card>
            {pendingPack.suggested_bin ? (
              <Card className="border-2 border-blue-300 bg-blue-50">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-1">Suggested Bin</p>
                  <p className="text-xl font-black text-[#1A1F2E] tracking-wider" data-testid="pack-suggested-bin">
                    {pendingPack.suggested_bin}
                  </p>
                  {pendingPack.bin_info?.suggestion_reason && (
                    <p className="text-xs text-blue-500 mt-1">{pendingPack.bin_info.suggestion_reason}</p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-red-300 bg-red-50">
                <CardContent className="p-3 text-center">
                  <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                  <p className="text-sm text-red-700 font-medium" data-testid="pack-no-bin-suggestion">No bin suggestion. Scan any valid bin.</p>
                </CardContent>
              </Card>
            )}
            <form onSubmit={handleBinScan}>
              <Card className="border-2 border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5 text-amber-700" />
                    <p className="text-sm font-semibold text-amber-800">Scan Bin to store the whole pack</p>
                  </div>
                  <Input
                    ref={binInputRef}
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value)}
                    placeholder="Scan bin barcode..."
                    className="text-lg h-12 border-amber-300 bg-white font-mono"
                    autoFocus
                    disabled={isProcessing}
                    data-testid="pack-bin-scan-input"
                  />
                  {isProcessing && <p className="text-xs text-amber-700 mt-2 animate-pulse">Storing pack...</p>}
                </CardContent>
              </Card>
            </form>
            <Button variant="outline" onClick={() => { setPendingPack(null); setMode('scan_usn'); }} className="w-full h-10 border-[#E5DFD0] text-[#5A5F6E]" data-testid="cancel-pack-bin-btn">
              Cancel
            </Button>
          </div>
        )}

        {mode === 'scan_bin' && pendingItem && (
          <div className="space-y-3" data-testid="bin-scan-section">
            {/* Item Info */}
            {pendingItem.usn_code !== '__override__' && pendingItem.usn_code !== '__bin_full_switch__' && (
              <Card className="border-[#E5DFD0]">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-[#5A5F6E]" />
                    <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">Scanned Item</p>
                  </div>
                  <p className="text-base font-bold text-[#1A1F2E]" data-testid="pending-item-design">{pendingItem.design_name}</p>
                  <div className="flex gap-3 text-xs text-[#5A5F6E] mt-0.5">
                    {pendingItem.shade && <span>Shade: {pendingItem.shade}</span>}
                    {pendingItem.size && <span>Size: {pendingItem.size}</span>}
                  </div>
                  <p className="text-xs text-[#B0A898] font-mono mt-1">USN: {pendingItem.usn_code}</p>
                </CardContent>
              </Card>
            )}

            {/* Bin Full Switch message */}
            {pendingItem.usn_code === '__bin_full_switch__' && (
              <Card className="border-2 border-amber-300 bg-amber-50/70">
                <CardContent className="p-3 text-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-amber-800">Previous bin marked full</p>
                  <p className="text-xs text-amber-600">Scan a new bin for {pendingItem.design_name}</p>
                </CardContent>
              </Card>
            )}

            {/* Suggested Bin */}
            {pendingItem.suggested_bin && (
              <Card className="border-2 border-blue-300 bg-blue-50">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-1">Suggested Bin</p>
                  <p className="text-xl font-black text-[#1A1F2E] tracking-wider" data-testid="suggested-bin">
                    {pendingItem.suggested_bin}
                  </p>
                  {pendingItem.bin_info?.suggestion_reason && (
                    <p className="text-xs text-blue-500 mt-1">{pendingItem.bin_info.suggestion_reason}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {!pendingItem.suggested_bin && pendingItem.usn_code !== '__override__' && pendingItem.usn_code !== '__bin_full_switch__' && (
              <Card className="border-2 border-red-300 bg-red-50">
                <CardContent className="p-3 text-center">
                  <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                  <p className="text-sm text-red-700 font-medium" data-testid="no-bin-suggestion">No bin suggestion. Scan any valid bin.</p>
                </CardContent>
              </Card>
            )}

            {/* Bin Scan Input */}
            <form onSubmit={getBinScanHandler()}>
              <Card className="border-2 border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5 text-amber-700" />
                    <p className="text-sm font-semibold text-amber-800">
                      {pendingItem.usn_code === '__override__' ? 'Scan New Bin' :
                       pendingItem.usn_code === '__bin_full_switch__' ? 'Scan New Bin for Same SKU' :
                       'Scan Bin to Confirm'}
                    </p>
                  </div>
                  <Input
                    ref={binInputRef}
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value)}
                    placeholder="Scan bin barcode..."
                    className="text-lg h-12 border-amber-300 bg-white font-mono"
                    autoFocus
                    disabled={isProcessing}
                    data-testid="bin-scan-input"
                  />
                  {isProcessing && (
                    <p className="text-xs text-amber-700 mt-2 animate-pulse">Placing...</p>
                  )}
                </CardContent>
              </Card>
            </form>

            <Button
              variant="outline"
              onClick={handleCancelBinScan}
              className="w-full h-10 border-[#E5DFD0] text-[#5A5F6E]"
              data-testid="cancel-bin-scan-btn"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* LAST ACTION FEEDBACK */}
        {lastAction && (mode === 'scan_usn' || mode === 'bin_active') && (
          <Card className="border-emerald-200 bg-emerald-50/50" data-testid="last-action">
            <CardContent className="p-2.5 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <div className="text-xs text-emerald-800">
                <span className="font-mono font-medium">{lastAction.usn}</span>
                <span className="mx-1">placed in</span>
                <span className="font-bold font-mono">{lastAction.bin}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Pack — group already-stored pieces (e.g. sales-return) into a pack */}
      <Dialog open={packDialogOpen} onOpenChange={setPackDialogOpen}>
        <DialogContent className="bg-white border-[#E5DFD0] mx-4 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <Boxes className="w-5 h-5 text-fuchsia-600" />
              Create {packSize}-pc Pack
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-[#5A5F6E]">Scan stored pieces (e.g. sales-return pieces you just put away), then scan the pre-printed pack code. This groups them so they pick as a pack later.</p>
            {packPieces[0] && (
              <div className="bg-[#F6F1E7] rounded-lg p-3" data-testid="clearance-pack-product">
                <p className="text-xs text-[#5A5F6E]">Product (locked)</p>
                <p className="font-semibold text-[#1A1F2E]">{packPieces[0].design_name || packPieces[0].sku_code}</p>
                <p className="text-xs text-[#5A5F6E]">{packPieces[0].sku_code} · {packPieces[0].shade} · {packPieces[0].size}</p>
              </div>
            )}
            <div className="flex items-center justify-between bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-3 py-2">
              <span className="text-sm text-fuchsia-800 font-medium">Scanned</span>
              <span className="text-xl font-bold text-fuchsia-700" data-testid="clearance-pack-count">{packPieces.length} / {packSize}</span>
            </div>
            <form onSubmit={scanPackPiece} className="flex gap-2">
              <Input ref={packPieceRef} value={packPieceInput} onChange={(e) => setPackPieceInput(e.target.value)} placeholder={packScanning ? 'Checking…' : 'Scan stored piece'} className="h-11 font-mono" data-testid="clearance-pack-piece-input" disabled={packScanning || packPieces.length >= packSize} />
              <Button type="submit" className="h-11 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={packScanning || packPieces.length >= packSize} data-testid="clearance-pack-piece-scan"><Scan className="w-5 h-5" /></Button>
            </form>
            {packPieces.length > 0 && (
              <div className="space-y-1 max-h-32 overflow-auto">
                {packPieces.map((p) => (
                  <div key={p.usn_code} className="flex items-center justify-between bg-[#F6F1E7] rounded px-2 py-1" data-testid={`clearance-pack-row-${p.usn_code}`}>
                    <span className="font-mono text-xs">{p.usn_code}</span>
                    <button onClick={() => removePackPiece(p.usn_code)} className="text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={submitRepackStored} className="flex gap-2 pt-1">
              <Input ref={packCodeRef} value={packCode} onChange={(e) => setPackCode(e.target.value.toUpperCase())} placeholder="Scan pack code" className="h-11 font-mono" data-testid="clearance-pack-code-input" />
              <Button type="submit" className="h-11 bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap" disabled={packCreating || !packPieces.length} data-testid="clearance-pack-create"><CheckCircle2 className="w-5 h-5 mr-1" /> Create</Button>
            </form>
            {packPieces.length > 0 && packPieces.length < packSize && (
              <p className="text-[11px] text-amber-600" data-testid="clearance-pack-short-note">Short pack: {packPieces.length} of {packSize}. Scan the pack code to finish.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGPutAwayClearance;
